/*
 * PHONE FIELD FORMATTING — for every intl-tel-input (v17) phone input of the storefront.
 *
 * The library formats a number when it is set (init, setNumber, country change) and only when its utils script
 * is loaded; it does not group the digits while typing, and a number set before the utils arrive stays raw
 * ("703568855"). The grouping is country specific (HU "70 356 8855", others differ), so it comes from the
 * utils' own data:
 *   - a complete, valid number: libphonenumber's international format without the dial code (the selector shows it)
 *   - a partial number: the group sizes of the country's example number, applied to the digits typed so far
 * It also keeps the field to what a phone number is made of (see the beforeinput filter).
 * It runs after the utils load, while typing (caret kept at the end), on blur and on a country change.
 */
export const PHONE_UTILS = '/js/phone-utils.js';

export function attachPhoneFormat(iti, input) {
    if (!iti || !input || input.dataset.phoneFormat) return;
    input.dataset.phoneFormat = '1';
    let applying = false;

    const utils = () => window.intlTelInputUtils;
    const country = () => iti.getSelectedCountryData() || {};

    // group sizes of the country's example number, e.g. HU [2, 3, 4]
    const groupsOf = (iso2, dial) => {
        const u = utils();
        if (!u || !iso2) return [];
        try {
            const example = u.getExampleNumber(iso2, false, u.numberFormat.INTERNATIONAL) || '';
            return example.replace('+' + dial, '').split(/[^0-9]+/).filter(Boolean).map((g) => g.length);
        } catch (e) {
            return [];
        }
    };

    const layout = (digits) => {
        const c = country();
        const u = utils();
        if (!u || !c.iso2) return null;
        try {
            const full = '+' + c.dialCode + digits;
            if (u.isValidNumber(full, c.iso2)) {
                const out = u.formatNumber(full, c.iso2, u.numberFormat.INTERNATIONAL).replace('+' + c.dialCode, '').trim();
                if (out) return out;
            }
        } catch (e) { /* the pattern below */ }
        const groups = groupsOf(c.iso2, c.dialCode);
        if (!groups.length) return null;
        const parts = [];
        let at = 0;
        for (const size of groups) {
            if (at >= digits.length) break;
            parts.push(digits.slice(at, at + size));
            at += size;
        }
        if (at < digits.length) parts.push(digits.slice(at));
        return parts.join(' ');
    };

    const apply = () => {
        if (applying) return;
        const before = input.value;
        // an international number typed or pasted with "+" is the library's to place; no digits, nothing to group
        if (before.includes('+') || !/\d/.test(before)) return;
        const out = layout(before.replace(/\D/g, ''));
        if (!out || out === before) return;
        const atEnd = input.selectionStart === null || input.selectionStart >= before.length;
        applying = true;
        input.value = out;
        if (atEnd) { try { input.setSelectionRange(out.length, out.length); } catch (e) { /* not focused */ } }
        // the form's model listens to input events
        input.dispatchEvent(new Event('input', { bubbles: true }));
        applying = false;
    };

    // What a phone field takes: digits and the separators people type, and a "+" at the very start (the library then
    // moves the number to that country: "+49 12345456" selects Germany). Letters and other symbols are refused as they
    // are typed or pasted; deletions carry no data and pass.
    input.addEventListener('beforeinput', (e) => {
        if (!e.data) return;
        if (/[^0-9+()\-.\s]/.test(e.data)) { e.preventDefault(); return; }
        if (e.data.includes('+')) {
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const kept = input.value.slice(0, start) + input.value.slice(end);
            if (input.value.slice(0, start).trim() !== '' || kept.includes('+')) e.preventDefault();
        }
    });
    input.addEventListener('input', apply);
    input.addEventListener('blur', apply);
    input.addEventListener('countrychange', apply);
    // a number set before the utils were there: format it once they are
    if (iti.promise && typeof iti.promise.then === 'function') iti.promise.then(apply).catch(() => {});
}
